/**
 * @file useStudentResponseObjectives.ts
 * @brief Custom hook for managing student responses for objectives, fetching data, and performing updates.
 */

// Import types
import { type Pagination } from "../../services/stores/types";
import {
  GetStudentResponseForStudent,
  GetStudentResponse,
  GetStudentResult,
  UpdateStudentResponse,
} from "./types/types";

// Import React hooks and react-query
import { useState, useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

// Import API object
import { studentResponseObjectivesAPI } from "./studentResponseObjectivesAPI";

/**
 * @brief Custom hook to manage student responses for objectives, providing functionalities to fetch, update, and paginate student response data.
 * @param token Authentication token for API requests.
 * @returns An object containing student responses, pagination information, loading status, error details, search functionality, and data manipulation functions.
 */
export const useStudentResponseObjectives = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });


  const queryKey = [
    "studentResponsesObjectives",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  // Busca todas as respostas com base em search e paginação
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      studentResponseObjectivesAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const updateStudentResponse = useMutation({
    mutationFn: ({ id, ...updateStudentResponseData }: UpdateStudentResponse) =>
      studentResponseObjectivesAPI.updateStudentResponse(
        id,
        updateStudentResponseData,
        token
      ),

    onSuccess: (updatedResponse, variables) => {
      refetch();
      queryClient.invalidateQueries({
        queryKey: ["studentResponsesObjectives"],
        exact: false,
      });

      queryClient.setQueryData(["studentResponsesObjectives"], (oldData: any) => {
        if (!oldData) return oldData;

        const newResponses = oldData.responses.map((resp: any) => {
          if (resp.id === variables.id) {
            return { ...resp, ...variables, ...(updatedResponse ?? {}) };
          }
          return resp;
        });

        return {
          ...oldData,
          responses: newResponses,
        };
      });
    },
  });


  const getResponsesByAnswerSheet = useCallback(
    async (
      answerSheetId: string,
      page: number = 1,
      perPage: number = 10,
      search?: string
    ): Promise<GetStudentResponseForStudent> => {
      return studentResponseObjectivesAPI.getAllByStudent(
        answerSheetId,
        token,
        page,
        perPage,
        search
      );
    },
    [token]
  );

  const getStudentResponsesByStudent = useCallback(
    async (
      answerSheetId: string,
      studentId: string,
      page: number = 1,
      perPage: number = 10,
      status?: string,
      search?: string
    ): Promise<GetStudentResponse> => {
      return studentResponseObjectivesAPI.getAllStudentResponsesByStudent(
        answerSheetId,
        studentId,
        token,
        page,
        perPage,
        status,
        search
      );
    },
    [token]
  );

  // Função para buscar resultados agregados com base no answerSheetId e termo de busca
  const getStudentResults = useCallback(
    async (
      answerSheetId: string,
      page: number = 1,
      perPage: number = 10,
      searchTerm?: string
    ): Promise<GetStudentResult> => {
      return studentResponseObjectivesAPI.getResults(
        answerSheetId,
        token,
        page,
        perPage,
        searchTerm
      );
    },
    [token]
  );

  return {
    studentResponses: data?.responses || [],
    pagination: data?.pagination || pagination,
    loading: isLoading || isFetching,
    error: isError ? error : null,
    search,
    setSearch: handleSearch,
    refetch,
    changePage,
    getResponsesByAnswerSheet,
    updateStudentResponse: updateStudentResponse.mutate,
    getStudentResults,
    getStudentResponsesByStudent,
  };
};